import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const FormW2GDueDates = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form W-2G deadlines for Tax Year 2025 | TaxZerone"
          description="File IRS Form W-2G for 2025 online with TaxZerone. Report all gambling winnings and any withheld taxes to stay fully compliant with IRS filing requirements."
          keywords=""
          canonical="resources/information-returns/form-w-2g-due-dates/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form W-2G</span>
          </span>{' '}
          Due Dates
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Report your Gambling Winnings before the deadline to avoid penalties
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=w2Gduedates`}
          >
            E-file Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-w-2g-due-dates/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form W-2G due dates at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        Casinos, lotteries, racetracks, and other entities that
                        pay qualifying gambling winnings are required to file
                        Form W-2G.
                      </span>
                    </li>
                    <li>
                      <span>
                        Winner copies must be furnished
                        {' by '}
                        <strong style={{ fontWeight: 600 }}>January 31</strong>
                        {', paper filing with the IRS must be '}
                        completed by
                        <strong style={{ fontWeight: 600 }}>
                          {' February 28'}
                        </strong>
                        , and electronic filing must be submitted
                        {' by'}
                        <strong style={{ fontWeight: 600 }}>
                          {' March 31'}
                        </strong>
                        .
                      </span>
                    </li>
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          {'Penalties: '}
                        </strong>
                        {'IRS penalties ranging from '}
                        <strong style={{ fontWeight: 600 }}>
                          $60 to $680 per return
                        </strong>
                        {
                          ' may be imposed for late, incorrect, or missing Form W-2G filings, depending on the timing and circumstances of the failure.'
                        }
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is Form W-2G?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Form W-2G</span>, is used
                  to{' '}
                  <span style={{ fontWeight: 600 }}>
                    report gambling winnings
                  </span>{' '}
                  and any{' '}
                  <span style={{ fontWeight: 600 }}>
                    federal income tax withheld
                  </span>{' '}
                  on those winnings. It must be filed by{' '}
                  <span style={{ fontWeight: 600 }}>
                    casinos, racetracks, lotteries, or any other entities
                  </span>{' '}
                  that award qualifying gambling prizes
                </Typography>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  This form helps the IRS and winners accurately track and
                  report gambling income. Winners must include the information
                  from Form W-2G when filing their federal tax returns.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Important Deadline for filing Form W-2G
                </Typography>

                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Filing type
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Deadlines
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Sending winner’s copy
                          </TableCell>

                          <TableCell sx={{}}>February 2, 2026 </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Paper filing
                          </TableCell>

                          <TableCell sx={{}}>March 2, 2026 </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            IRS e-filing
                          </TableCell>

                          <TableCell sx={{}}>March 31, 2026 </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert severity="warning" icon={false} sx={{ mt: 2, mb: 4 }}>
                  <b>Note:</b> If any deadline falls on a weekend or federal
                  holiday, it is extended to the next business day.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Penalties
                </Typography>

                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Scenario
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Penalty per return
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Penalty per year
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Failure to file on time
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $60
                          </TableCell>

                          <TableCell sx={{}}>
                            $683,000 ($239,000 for small business){' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            after 30 days before August 1
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $130
                          </TableCell>

                          <TableCell sx={{}}>
                            $2,049,000 ($683,000 for small business){' '}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            after August 1 or not filed
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $340
                          </TableCell>

                          <TableCell sx={{}}>
                            $4,098,500 ($1,366,000 for small business){' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Intentional Disregard
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $680
                          </TableCell>

                          <TableCell sx={{}}>No limit </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Filing Form W-2G with TaxZerone
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Step 1:</span> Login to your{' '}
                  <Link
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=w2Gduedates`}
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    TaxZerone Account
                  </Link>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Step 2:</span> Choose Form
                  W-2G and the Tax year including details of payers and winner’s
                  name, address and TIN along with the Gambling Winnings
                  information
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Step 3:</span> Submit the
                  form to the IRS and issue the winner’s copy via ZeroneVault or
                  send it to the mail.
                </Typography>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information given above is based on the IRS
                        guidelines. For more details, please refer the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/iw2g.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          instructions of Form W-2G
                        </Link>{' '}
                        and the{' '}
                        <Link
                          href="https://www.irs.gov/instructions/i1099gi"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          Certain instructions of Information returns
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to file Form W-2G?
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
                lineHeight: 1.4,
              }}
            >
              File now with TaxZerone to stay compliant with IRS regulations
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=w2Gduedates`}
              >
                E-file Now!
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default FormW2GDueDates;
